import React, { type FC, memo, useCallback } from "react";
import { shallowEqual } from "react-redux";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import styles from "./Overview.module.scss";
import a11y from "../../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../redux/store";
import Price from "../../price/Price";
import { removeExtra } from "../../../../../redux/slices/CarConfigBasketSlice";
import Pack from "./Pack";
import { CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import { trackSummaryCardRemoveExtra } from "../../../../../utils/tracking";
import { scrollToTarget } from "../../../../../../../../common-deprecated/utils/scrollUtils";
import Icon from "../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import { showAccessoriesRate } from "../../../../../utils/financeInsuranceRate";
import { setActiveSection } from "../../../../../redux/slices/CarConfigNavigationSlice";
import { CarConfigLabel } from "../../../../../utils/labelUtils";
import { getPacksVisual } from "../../../../../utils/visualUtils";
import { useComponentRegistry } from "../../../componentRegistry";
import { CarConfigConfigureSectionsLbx } from "../../../../../utils/types/SectionTypesLbx";
import { hasPacksOrExtrasSelector } from "../../../../../utils/selectorUtils";

const OverviewPacksAndExtras: FC = (): JSX.Element | null => {
    const dispatch = useCarConfigDispatch();

    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const packs = useCarConfigSelector((state) => state.basket.packs);
    const extras = useCarConfigSelector(
        (state) => state.basket.extras.filter((extra) => !extra.isCharger),
        shallowEqual,
    );
    const useLbxJourney = useCarConfigSelector((state) => state.commonSettings.query.useLbxJourney || false);
    const standardPacks = useCarConfigSelector(
        (state) => state.items.packs.filter((pack) => pack.standard),
        shallowEqual,
    );

    const hasPacksOrExtras = extras.length !== 0 || packs.length !== 0 || standardPacks.length !== 0;
    const showPacksAndExtras = useCarConfigSelector(hasPacksOrExtrasSelector, shallowEqual);

    const handleClick = useCallback(() => {
        scrollToTarget(useLbxJourney ? CarConfigConfigureSectionsLbx.Packs : CarConfigConfigureSection.Packs);

        const verticalScroll = window.matchMedia("(min-width: 1200px)");
        if (!verticalScroll.matches) {
            dispatch(
                setActiveSection(useLbxJourney ? CarConfigConfigureSectionsLbx.Packs : CarConfigConfigureSection.Packs),
            );
        }
    }, [dispatch]);

    const imagePacks = getPacksVisual([...packs, ...standardPacks], extras, commonSettings);
    const OverviewItemWrapper = useComponentRegistry("OverviewItemWrapper");

    if (!showPacksAndExtras) return null;

    return (
        <OverviewItemWrapper
            title="carConfigOverviewTilePacks"
            editSection={useLbxJourney ? CarConfigConfigureSectionsLbx.Packs : CarConfigConfigureSection.Packs}
            image={imagePacks}
        >
            <div>
                {hasPacksOrExtras ? (
                    <ul role="list" className={styles.sublist}>
                        {standardPacks.map((pack) => (
                            <Pack key={pack.id} pack={pack} />
                        ))}
                        {packs.map((pack) => (
                            <Pack key={pack.id} pack={pack} />
                        ))}

                        {extras.map((extra) => (
                            <li key={extra.id} className={styles.listitem}>
                                <div>
                                    <Text variant="apheleia-body-small" spacing="spacing-0">
                                        {extra.name}
                                    </Text>
                                </div>
                                <div className={styles.listprice}>
                                    <Text variant="apheleia-body" spacing="spacing-0">
                                        {extra.standard || extra.price.cash === 0 ? (
                                            <span className={styles.included}>
                                                <CarConfigLabel label="carConfigIncluded" />
                                                <span className={styles["included-icon"]}>
                                                    <Icon variant="check" sizing="sm" aria-hidden="true" />
                                                </span>
                                            </span>
                                        ) : (
                                            <span className={styles["listprice-price"]}>
                                                +&nbsp;
                                                <Price
                                                    price={extra.price}
                                                    forceCashPrice={
                                                        !showAccessoriesRate(commonSettings, carConfigSettings)
                                                    }
                                                />
                                            </span>
                                        )}
                                    </Text>
                                </div>
                                <div className={styles["listitem-actions"]}>
                                    <Button
                                        variant="btn-default"
                                        circle
                                        sizing="xs"
                                        onClick={() => {
                                            trackSummaryCardRemoveExtra();
                                            dispatch(removeExtra({ extra }));
                                        }}
                                    >
                                        <Icon variant="minus" sizing="sm" color="primary-white" aria-hidden="true" />
                                        <span className={a11y["sr-only"]}>
                                            <CarConfigLabel label="carConfigOverviewPacksRemove" />
                                        </span>
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <Button variant="apheleia-text" onClick={handleClick}>
                        <CarConfigLabel label="carConfigOverviewPacksBrowse" />
                    </Button>
                )}
            </div>
        </OverviewItemWrapper>
    );
};

export default memo(OverviewPacksAndExtras);
